import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { initializeSmaWithTemplate } from '../store/slices/sma.slice';
import useSma from '../pages/SmaV2/useSma';
/**
 * Central hook for feature-specific functionality across the application.
 * Provides methods for initializing and managing different features.
 *
 * @returns {Object} Object containing feature initialization methods
 */
export const useFeatures = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addEtf } = useSma();

  /**
   * Initializes a new SMA configuration with provided ETFs and navigates to the SMA builder.
   *
   * @param {Array<string|Object>} items - Array of ETF identifiers. Can be either:
   *   - Array of strings: ['VOO', 'VTI', 'QQQ']
   *   - Array of objects: [{symbol: 'VOO', wgt: 0.4}, {symbol: 'VTI', wgt: 0.6}]
   * @param {Object} options - Additional configuration options
   * @param {string} [options.linkedPortfolioId] - ID of a portfolio to link to this SMA
   * @param {string} [options.navigatePath='/builders/sma'] - Custom navigation path after initialization
   *
   * @example
   * // With array of symbols (equal weights)
   * initializeSma(['VOO', 'VTI', 'QQQ']);
   *
   * @example
   * // With weighted objects
   * initializeSma([
   *   { symbol: 'VOO', wgt: 0.4 },
   *   { symbol: 'VTI', wgt: 0.6 }
   * ]);
   *
   * @example
   * // With custom options
   * initializeSma(['VOO', 'VTI'], {
   *   linkedPortfolioId: 'portfolio_123',
   *   navigatePath: '/custom/sma/path'
   * });
   */
  const initializeSma = useCallback((items, options = {}) => {
    // Handle both array of symbols or array of {symbol, wgt} objects
    console.log('items', items)
    const formattedEtfs = items.map(item => {
      if (typeof item === 'string') {
        return {
          symbol: item.toLowerCase(),
          weight: 1 / items.length // Default to equal weight
        };
      }

      // Assume it's an object with symbol and wgt
      return {
        symbol: item.symbol.toLowerCase(),
        weight: item.wgt || (1 / items.length)
      };
    });
    formattedEtfs.forEach(item=>{
      addEtf(item.symbol, item.weight)
    })
    // console.log('formattedEtfs', formattedEtfs)

    dispatch(initializeSmaWithTemplate({
      etfs: formattedEtfs,
    }));


    navigate(options?.navigatePath || '/builders/sma');
  }, [dispatch, navigate]);

  return {
    initializeSma,
  };
};

export default useFeatures;